import React from "react";
import * as Yup from "yup";
import yupSchema from "../../../constants/schema";
import { useErrorHandler } from "../../../hooks";
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Paper,
  Typography,
  Link,
} from "@material-ui/core";
import DialogTitle from "../../../components/dialog-title";
import FormBuilder from "../../../components/form-builder";
import coordinationService from "../../../services/coordination.service";
import { addDays } from "../../../helpers/date-helpers";
import { useMemo } from "react";
import { currencyFormat } from "../../../helpers";
import { useSelector } from "react-redux";

const schema = Yup.object().shape({
  description: yupSchema.description,
  prescriptionDescription: yupSchema.description,
  validDays: yupSchema.validDays,
  items: yupSchema.lenses,
});

function InsuranceLink(props) {
  const { user, insurance } = props;
  const { name, url } = insurance || {};

  const link = useMemo(() => {
    if (!url) return;
    return url.replaceAll("{nationalCode}", user.nationalCode);
  }, [url, user.nationalCode]);

  if (!name) return "";
  if (!link) return name;
  return (
    <Link href={link} target="_blank" rel="noreferrer">
      {name}
    </Link>
  );
}

export default function FormModalContent({ close, data, extra }) {
  const { id, user, status, description, glass, items, prescription, userDescription } = data;

  const { initials } = useSelector((s) => s.app);
  const errorHandler = useErrorHandler();
  const initialValues = useMemo(
    () => ({
      items: [
        {
          lensId: undefined,
          price: 0,
          insuranceContribution: 0,
          // expireDate: Date
        },
      ],
      validDays: initials.coordinationExpDays || 10,
      description: "",
      prescriptionDescription: prescription.description || "",
    }),
    [user, initials]
  );
  const form = useMemo(
    () => [
      {
        _type: "textField",
        multiline: true,
        rows: 4,
        name: "prescriptionDescription",
        label: "توضیحات نسخه",
      },
      {
        _type: "textField",
        multiline: true,
        rows: 4,
        name: "description",
        label: "توضیحات",
        autoFocus: true,
      },
      {
        _type: "textField",
        type: "number",
        name: "validDays",
        label: "روز های قابل قبول",
      },
      {
        _type: "array",
        _wrapper: (props) => <Grid item xs={12} {...props} />,
        _itemsWrapper: (props) => (
          <Grid item xs>
            <Grid container spacing={2} {...props} />
          </Grid>
        ),
        name: "items",
        label: "عدسی‌ها",
        dense: true,
        minLength: 1,
        emptyValue: {
          lensId: undefined,
          price: 0,
          insuranceContribution: 0,
        },
        items: [
          {
            _type: "autocomplete",
            _wrapper: (props) => <Grid item xs={12} sm={4} {...props} />,
            name: "lensId",
            label: "لنز",
            options: extra.lenses,
            valueSelector: "id",
            labelSelector: "name",
          },
          {
            _type: "textField",
            _wrapper: (props) => <Grid item xs={12} sm={4} {...props} />,
            name: "price",
            label: "مبلغ",
            type: "currency",
          },
          {
            _type: "textField",
            _wrapper: (props) => <Grid item xs={12} sm={4} {...props} />,
            name: "insuranceContribution",
            label: "سهم بیمه",
            type: "currency",
          },
        ],
      },
    ],
    [extra]
  );
  const lenses = useMemo(() => {
    if (!extra) return {};
    const obj = {};
    extra.lenses.forEach((l) => (obj[l.id] = l.name));
    return obj;
  }, [extra]);

  const handleSubmit = async (model, { setSubmitting }, rejected) => {
    const { items, validDays, description, prescriptionDescription } = model;
    const { error } = await coordinationService.coordinate(
      id,
      rejected
        ? {
            description,
            prescriptionDescription,
            status: "rejected",
          }
        : {
            description,
            prescriptionDescription,
            status: "accepted",
            expireDate: addDays(validDays),
            items,
          }
    );
    setSubmitting(false);
    if (error) return errorHandler(error);

    close(true);
  };

  return (
    // <LoadingComponent isLoading={waiting} overlay>
    <>
      <DialogTitle onClose={() => close()}>هماهنگی‌</DialogTitle>
      <Divider />
      <DialogContent>
        <img src={prescription.image} alt="" />
        <Grid container>
          <Grid item xs={6}>
            <Typography>
              بیمه پایه:
              {
                (user && user?.basicInsurance) &&
                    <InsuranceLink user={user} insurance={user?.basicInsurance} />
              }
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography>
              بیمه تکمیلی:
              {
                (user && user?.basicInsurance) &&
                    <InsuranceLink user={user} insurance={user?.basicInsurance} />
              }
            </Typography>
          </Grid>
        </Grid>
        <Grid container className="mt-2">
          <Grid item xs={12}>
            <Typography>
              توضیحات کاربر:
              { userDescription }
            </Typography>
          </Grid>
        </Grid>
        {status === "pending" ? (
          <FormBuilder
            schema={schema}
            initialValues={initialValues}
            handleSubmit={handleSubmit}
            form={form}
            actionComponent={({ isSubmitting, setSubmitting, values }) => (
              <DialogActions>
                <Button onClick={close} color="primary" disabled={isSubmitting}>
                  لغو
                </Button>
                <Button
                  color="primary"
                  variant="outlined"
                  disabled={isSubmitting}
                  onClick={() => {
                    handleSubmit(values, { setSubmitting }, true);
                  }}
                >
                  رد کردن
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="contained"
                  color="primary"
                >
                  تایید
                </Button>
              </DialogActions>
            )}
          />
        ) : (
          <div>
            <Typography component="h2" variant="h5">
              عینک {glass?.name}
            </Typography>
            {!!prescription.description && (
              <Paper className="my-4 p-2" variant="outlined">
                <Typography component="h3" variant="h6">
                  توضیحات نسخه
                </Typography>
                <Divider className="my-2" />
                <Typography>{prescription.description}</Typography>
              </Paper>
            )}
            {!!description && (
              <Paper className="my-4 p-2" variant="outlined">
                <Typography component="h3" variant="h6">
                  توضیحات
                </Typography>
                <Divider className="my-2" />
                <Typography>{description}</Typography>
              </Paper>
            )}
            <Paper className="my-4 p-2" variant="outlined">
              <Typography component="h3" variant="h6">
                لنز‌ها
              </Typography>
              <Divider className="mt-2" />
              {items && items?.map((item) => (
                <div className="d-flex space-between mt-2" key={item.id}>
                  <Typography>{lenses[item.lensId]}</Typography>
                  <Typography>{currencyFormat(item.price)}</Typography>
                </div>
              ))}
            </Paper>
          </div>
        )}
      </DialogContent>
    </>
    // </LoadingComponent>
  );
}
